// frontend/src/utils/locationService.js - Enhanced Location Utility Service
import api from '@/services/api';
import { useLocation } from '@/composables/useLocation';

class LocationService {
  constructor() {
    this.isInitialized = false;
    this.locationComposable = null;
    this.trackingActive = false;
    this.lastKnownLocation = null;
    this.callbacks = {
      onLocationUpdate: [],
      onLocationError: [],
      onPermissionChange: [],
    };

    // Constants
    this.OFFICE_LOCATIONS = {
      main: {
        name: 'Main Office',
        latitude: 31.9073,
        longitude: 35.2044,
        radius: 100, // meters
      },
      branch: {
        name: 'Branch Office',
        latitude: 31.95,
        longitude: 35.2333,
        radius: 150,
      },
    };

    this.FIELD_LOCATIONS = [
      {
        id: 1,
        name: 'Main Construction Site',
        latitude: 31.92,
        longitude: 35.21,
        radius: 200,
      },
      {
        id: 2,
        name: 'Client Meeting Location',
        latitude: 31.88,
        longitude: 35.19,
        radius: 100,
      },
    ];

    this.LOCATION_ACCURACY_THRESHOLD = 50; // meters
    this.HIGH_ACCURACY_THRESHOLD = 20; // meters
    this.TRACKING_INTERVAL = 30000; // 30 seconds
  }

  // ============================================================================
  // INITIALIZATION & SETUP
  // ============================================================================

  /**
   * Initialize the location service
   * @param {Object} options - Configuration options
   * @returns {Promise<boolean>} Success status
   */
  async initialize(options = {}) {
    try {
      console.log('🌍 Initializing Location Service...');

      if (this.isInitialized) {
        console.log('⚠️ Location service already initialized');
        return true;
      }

      // Initialize the composable
      this.locationComposable = useLocation();

      // checkSupport() must be called first — isSupported starts as ref(false)
      this.locationComposable.checkSupport();

      // Check support
      if (!this.locationComposable.isSupported.value) {
        console.warn('❌ Geolocation not supported');
        return false;
      }

      // Set up event listeners
      this.setupEventListeners();

      // Request permission if needed
      if (options.requestPermission !== false) {
        await this.requestLocationPermission();
      }

      this.isInitialized = true;
      console.log('✅ Location Service initialized successfully');
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize location service:', error);
      return false;
    }
  }

  /**
   * Setup event listeners for location updates
   */
  setupEventListeners() {
    // Listen for permission changes
    if ('permissions' in navigator) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then(result => {
          result.addEventListener('change', () => {
            this.notifyCallbacks('onPermissionChange', result.state);
          });
        })
        .catch(console.warn);
    }
  }

  // ============================================================================
  // PERMISSION MANAGEMENT
  // ============================================================================

  /**
   * Request location permission from user
   * @returns {Promise<boolean>} Permission granted status
   */
  async requestLocationPermission() {
    try {
      if (!this.locationComposable) {
        throw new Error('Location service not initialized');
      }

      const granted = await this.locationComposable.requestPermission();

      if (granted) {
        console.log('✅ Location permission granted');
        this.notifyCallbacks('onPermissionChange', 'granted');
      } else {
        console.log('❌ Location permission denied');
        this.notifyCallbacks('onPermissionChange', 'denied');
      }

      return granted;
    } catch (error) {
      console.error('❌ Permission request failed:', error);
      this.notifyCallbacks('onLocationError', error);
      return false;
    }
  }

  /**
   * Check current permission status
   * @returns {string} Permission status
   */
  getPermissionStatus() {
    return this.locationComposable?.permissionStatus?.value || 'unknown';
  }

  // ============================================================================
  // LOCATION RETRIEVAL
  // ============================================================================

  /**
   * Get current location once
   * @param {Object} options - Geolocation options
   * @returns {Promise<Object|null>} Location data
   */
  async getCurrentLocation(options = {}) {
    try {
      if (!this.locationComposable) {
        throw new Error('Location service not initialized');
      }

      const defaultOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000, // Accept 1-minute old location
      };

      const location = await this.locationComposable.getCurrentPosition({
        ...defaultOptions,
        ...options,
      });

      if (location) {
        this.lastKnownLocation = this.enrichLocationData(location);
        this.notifyCallbacks('onLocationUpdate', this.lastKnownLocation);
        return this.lastKnownLocation;
      }

      return null;
    } catch (error) {
      console.error('❌ Failed to get current location:', error);
      this.notifyCallbacks('onLocationError', error);
      return null;
    }
  }

  /**
   * Start continuous location tracking
   * @param {Object} options - Tracking options
   * @returns {boolean} Success status
   */
  startLocationTracking(options = {}) {
    try {
      if (!this.locationComposable) {
        throw new Error('Location service not initialized');
      }

      if (this.trackingActive) {
        console.log('⚠️ Location tracking already active');
        return true;
      }

      const defaultOptions = {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      };

      const success = this.locationComposable.startTracking({
        ...defaultOptions,
        ...options,
      });

      if (success) {
        this.trackingActive = true;
        console.log('🎯 Location tracking started');

        // Set up periodic location enrichment
        this.setupLocationEnrichment();
      }

      return success;
    } catch (error) {
      console.error('❌ Failed to start location tracking:', error);
      this.notifyCallbacks('onLocationError', error);
      return false;
    }
  }

  /**
   * Stop location tracking
   */
  stopLocationTracking() {
    try {
      if (this.locationComposable) {
        this.locationComposable.stopTracking();
      }

      this.trackingActive = false;
      console.log('🛑 Location tracking stopped');
    } catch (error) {
      console.error('❌ Failed to stop location tracking:', error);
    }
  }

  /**
   * Setup periodic location enrichment
   */
  setupLocationEnrichment() {
    // Monitor location changes and enrich data
    const checkLocationUpdates = () => {
      if (
        this.trackingActive &&
        this.locationComposable?.currentLocation?.value
      ) {
        const currentLoc = this.locationComposable.currentLocation.value;

        // Only process if location actually changed
        if (this.hasLocationChanged(currentLoc)) {
          const enrichedLocation = this.enrichLocationData(currentLoc);
          this.lastKnownLocation = enrichedLocation;
          this.notifyCallbacks('onLocationUpdate', enrichedLocation);
        }
      }
    };

    // Check every 10 seconds
    setInterval(checkLocationUpdates, 10000);
  }

  // ============================================================================
  // LOCATION DATA ENRICHMENT
  // ============================================================================

  /**
   * Enrich location data with additional context
   * @param {Object} locationData - Raw location data
   * @returns {Object} Enriched location data
   */
  enrichLocationData(locationData) {
    if (!locationData || !locationData.latitude) {
      return null;
    }

    const enriched = {
      ...locationData,
      // Add context
      isInOffice: this.isInOfficeLocation(locationData),
      currentFieldLocation: this.getCurrentFieldLocation(locationData),
      nearestOffice: this.getNearestOffice(locationData),
      locationMode: 'unknown',
      accuracy: locationData.accuracy || null,
      isHighAccuracy:
        (locationData.accuracy || 999) <= this.HIGH_ACCURACY_THRESHOLD,
      trustScore: this.calculateLocationTrustScore(locationData),
      timestamp: locationData.timestamp || Date.now(),
    };

    // Determine location mode
    if (enriched.isInOffice) {
      enriched.locationMode = 'office';
    } else if (enriched.currentFieldLocation) {
      enriched.locationMode = 'field';
    } else {
      enriched.locationMode = 'remote';
    }

    return enriched;
  }

  /**
   * Check if location has significantly changed
   * @param {Object} newLocation - New location data
   * @returns {boolean} Has changed
   */
  hasLocationChanged(newLocation) {
    if (!this.lastKnownLocation || !newLocation) {
      return true;
    }

    const distance = this.calculateDistance(
      this.lastKnownLocation.latitude,
      this.lastKnownLocation.longitude,
      newLocation.latitude,
      newLocation.longitude
    );

    // Consider changed if moved more than 10 meters
    return distance > 10;
  }

  // ============================================================================
  // GEOFENCING & LOCATION ANALYSIS
  // ============================================================================

  /**
   * Check if location is within office boundaries
   * @param {Object} location - Location data
   * @returns {boolean} Is in office
   */
  isInOfficeLocation(location) {
    if (!location?.latitude) return false;

    return Object.values(this.OFFICE_LOCATIONS).some(office =>
      this.isWithinRadius(location, office, office.radius)
    );
  }

  /**
   * Get current field location if within any field site
   * @param {Object} location - Location data
   * @returns {Object|null} Field location
   */
  getCurrentFieldLocation(location) {
    if (!location?.latitude) return null;

    return (
      this.FIELD_LOCATIONS.find(field =>
        this.isWithinRadius(location, field, field.radius)
      ) || null
    );
  }

  /**
   * Get nearest office location
   * @param {Object} location - Location data
   * @returns {Object|null} Nearest office with distance
   */
  getNearestOffice(location) {
    if (!location?.latitude) return null;

    let nearest = null;
    let minDistance = Infinity;

    Object.entries(this.OFFICE_LOCATIONS).forEach(([key, office]) => {
      const distance = this.calculateDistance(
        location.latitude,
        location.longitude,
        office.latitude,
        office.longitude
      );

      if (distance < minDistance) {
        minDistance = distance;
        nearest = {
          ...office,
          key,
          distance,
        };
      }
    });

    return nearest;
  }

  /**
   * Check if location is within radius of a point
   * @param {Object} location - Current location
   * @param {Object} center - Center point
   * @param {number} radius - Radius in meters
   * @returns {boolean} Is within radius
   */
  isWithinRadius(location, center, radius) {
    const distance = this.calculateDistance(
      location.latitude,
      location.longitude,
      center.latitude,
      center.longitude
    );
    return distance <= radius;
  }

  /**
   * Calculate distance between two points using Haversine formula
   * @param {number} lat1 - First latitude
   * @param {number} lon1 - First longitude
   * @param {number} lat2 - Second latitude
   * @param {number} lon2 - Second longitude
   * @returns {number} Distance in meters
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371000; // Earth's radius in meters
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) *
        Math.cos(this.toRadians(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Convert degrees to radians
   * @param {number} degrees - Degrees value
   * @returns {number} Radians value
   */
  toRadians(degrees) {
    return degrees * (Math.PI / 180);
  }

  // ============================================================================
  // SECURITY & VALIDATION
  // ============================================================================

  /**
   * Calculate location trust score
   * @param {Object} location - Location data
   * @returns {number} Trust score (0-100)
   */
  calculateLocationTrustScore(location) {
    let score = 100;

    // Accuracy penalty
    if (location.accuracy > this.LOCATION_ACCURACY_THRESHOLD) {
      score -= 20;
    }
    if (location.accuracy > 100) {
      score -= 30;
    }

    // Age penalty
    if (location.timestamp) {
      const age = Date.now() - location.timestamp;
      if (age > 60000) {
        // 1 minute
        score -= 10;
      }
      if (age > 300000) {
        // 5 minutes
        score -= 20;
      }
    }

    // Speed validation
    if (location.speed > 50) {
      // 50 m/s = 180 km/h
      score -= 40; // Suspicious speed
    }

    // Coordinate precision check (too precise might indicate spoofing)
    if (location.latitude && location.longitude) {
      const latStr = location.latitude.toString();
      const lonStr = location.longitude.toString();

      if (
        latStr.split('.')[1]?.length > 10 ||
        lonStr.split('.')[1]?.length > 10
      ) {
        score -= 15; // Suspiciously precise
      }
    }

    return Math.max(0, Math.min(100, score));
  }

  /**
   * Validate location data
   * @param {Object} location - Location data
   * @returns {Object} Validation result
   */
  validateLocation(location) {
    const validation = {
      isValid: true,
      issues: [],
      trustScore: 100,
    };

    if (!location) {
      validation.isValid = false;
      validation.issues.push('No location data provided');
      validation.trustScore = 0;
      return validation;
    }

    // Coordinate validation
    if (!location.latitude || !location.longitude) {
      validation.isValid = false;
      validation.issues.push('Missing coordinates');
      validation.trustScore = 0;
      return validation;
    }

    if (location.latitude < -90 || location.latitude > 90) {
      validation.isValid = false;
      validation.issues.push('Invalid latitude');
      validation.trustScore = 0;
    }

    if (location.longitude < -180 || location.longitude > 180) {
      validation.isValid = false;
      validation.issues.push('Invalid longitude');
      validation.trustScore = 0;
    }

    // Accuracy validation
    if (location.accuracy > this.LOCATION_ACCURACY_THRESHOLD) {
      validation.issues.push('Low accuracy');
      validation.trustScore -= 20;
    }

    if (location.accuracy > 500) {
      validation.isValid = false;
      validation.issues.push('Accuracy too poor');
      validation.trustScore -= 50;
    }

    return validation;
  }

  // ============================================================================
  // SERVER INTEGRATION
  // ============================================================================

  /**
   * Send location data to server
   * @param {Object} locationData - Location data to send
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Server response
   */
  async sendLocationToServer(locationData, sessionId) {
    try {
      const enrichedData = this.enrichLocationData(locationData);

      const payload = {
        sessionId,
        location: {
          latitude: enrichedData.latitude,
          longitude: enrichedData.longitude,
          accuracy: enrichedData.accuracy,
          altitude: enrichedData.altitude,
          speed: enrichedData.speed,
          heading: enrichedData.heading,
          timestamp: enrichedData.timestamp,
        },
        context: {
          locationMode: enrichedData.locationMode,
          isInOffice: enrichedData.isInOffice,
          currentFieldLocation: enrichedData.currentFieldLocation,
          trustScore: enrichedData.trustScore,
        },
      };

      const response = await api.post('/session/location', payload);

      if (response.data.success) {
        console.log('✅ Location sent to server successfully');
      }

      return response.data;
    } catch (error) {
      console.error('❌ Failed to send location to server:', error);
      this.notifyCallbacks('onLocationError', error);
      throw error;
    }
  }

  /**
   * Update session location periodically
   * @param {string} sessionId - Session ID
   * @param {number} interval - Update interval in milliseconds
   */
  startServerLocationUpdates(sessionId, interval = this.TRACKING_INTERVAL) {
    if (!sessionId) {
      console.warn('⚠️ No session ID provided for location updates');
      return;
    }

    const updateLocation = async () => {
      try {
        if (this.lastKnownLocation && this.trackingActive) {
          await this.sendLocationToServer(this.lastKnownLocation, sessionId);
        }
      } catch (error) {
        console.warn('⚠️ Location update failed:', error.message);
      }
    };

    // Initial update
    setTimeout(updateLocation, 2000); // Wait 2 seconds after start

    // Periodic updates
    const intervalId = setInterval(updateLocation, interval);

    // Store interval ID for cleanup
    this.updateIntervalId = intervalId;

    console.log(`🔄 Started server location updates (${interval}ms interval)`);
  }

  /**
   * Stop server location updates
   */
  stopServerLocationUpdates() {
    if (this.updateIntervalId) {
      clearInterval(this.updateIntervalId);
      this.updateIntervalId = null;
      console.log('🛑 Stopped server location updates');
    }
  }

  // ============================================================================
  // EVENT HANDLING & CALLBACKS
  // ============================================================================

  /**
   * Register callback for location events
   * @param {string} event - Event type
   * @param {Function} callback - Callback function
   */
  addEventListener(event, callback) {
    if (this.callbacks[event]) {
      this.callbacks[event].push(callback);
    }
  }

  /**
   * Remove callback for location events
   * @param {string} event - Event type
   * @param {Function} callback - Callback function
   */
  removeEventListener(event, callback) {
    if (this.callbacks[event]) {
      const index = this.callbacks[event].indexOf(callback);
      if (index > -1) {
        this.callbacks[event].splice(index, 1);
      }
    }
  }

  /**
   * Notify all callbacks for an event
   * @param {string} event - Event type
   * @param {*} data - Event data
   */
  notifyCallbacks(event, data) {
    if (this.callbacks[event]) {
      this.callbacks[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`❌ Callback error for ${event}:`, error);
        }
      });
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Format coordinates for display
   * @param {number} lat - Latitude
   * @param {number} lon - Longitude
   * @param {number} precision - Decimal places
   * @returns {string} Formatted coordinates
   */
  formatCoordinates(lat, lon, precision = 6) {
    return `${lat.toFixed(precision)}, ${lon.toFixed(precision)}`;
  }

  /**
   * Format distance for display
   * @param {number} meters - Distance in meters
   * @returns {string} Formatted distance
   */
  formatDistance(meters) {
    if (meters < 1000) {
      return `${Math.round(meters)} m`;
    }
    return `${(meters / 1000).toFixed(1)} km`;
  }

  /**
   * Get location mode display text
   * @param {string} mode - Location mode
   * @returns {string} Display text
   */
  getLocationModeDisplay(mode) {
    const modes = {
      office: 'In Office',
      field: 'On Site',
      remote: 'Remote Work',
      unknown: 'Unknown Location',
    };
    return modes[mode] || modes.unknown;
  }

  /**
   * Get current location summary
   * @returns {Object} Location summary
   */
  getLocationSummary() {
    if (!this.lastKnownLocation) {
      return {
        mode: 'unknown',
        display: 'Location not available',
        coordinates: null,
        accuracy: null,
        timestamp: null,
      };
    }

    const loc = this.lastKnownLocation;
    return {
      mode: loc.locationMode,
      display: this.getLocationModeDisplay(loc.locationMode),
      coordinates: this.formatCoordinates(loc.latitude, loc.longitude),
      accuracy: loc.accuracy ? this.formatDistance(loc.accuracy) : 'Unknown',
      timestamp: new Date(loc.timestamp).toLocaleTimeString(),
      isHighAccuracy: loc.isHighAccuracy,
      trustScore: loc.trustScore,
    };
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  /**
   * Cleanup and destroy service
   */
  destroy() {
    console.log('🧹 Cleaning up Location Service...');

    this.stopLocationTracking();
    this.stopServerLocationUpdates();

    // Clear callbacks
    Object.keys(this.callbacks).forEach(event => {
      this.callbacks[event] = [];
    });

    this.isInitialized = false;
    this.locationComposable = null;
    this.lastKnownLocation = null;

    console.log('✅ Location Service cleaned up');
  }
}

// Create singleton instance
const locationService = new LocationService();

// Export both the class and singleton instance
export { LocationService, locationService as default };

// Export commonly used methods for convenience
export const {
  initialize,
  getCurrentLocation,
  startLocationTracking,
  stopLocationTracking,
  sendLocationToServer,
  getLocationSummary,
  formatCoordinates,
  formatDistance,
  calculateDistance,
} = locationService;
